import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Eye, Copy, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../UI/Button';
import { albumsApi } from '../../api/albums';
import { ReportReason } from '../../types';
import styles from './PhotoReportSheet.module.css';

interface PhotoReportSheetProps {
  isOpen: boolean;
  onClose: () => void;
  albumId: string;
  photoId: string;
  onReported: () => void;
}

const REASON_OPTIONS: { value: ReportReason; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'inappropriate',
    label: 'Inappropriate content',
    description: 'Offensive, harmful, or NSFW material',
    icon: <AlertTriangle style={{ width: 16, height: 16 }} />,
  },
  {
    value: 'privacy',
    label: 'Privacy concern',
    description: 'Contains identifiable info I want removed',
    icon: <Eye style={{ width: 16, height: 16 }} />,
  },
  {
    value: 'duplicate',
    label: 'Duplicate photo',
    description: 'This photo appears more than once',
    icon: <Copy style={{ width: 16, height: 16 }} />,
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Another reason not listed above',
    icon: <HelpCircle style={{ width: 16, height: 16 }} />,
  },
];

export function PhotoReportSheet({ isOpen, onClose, albumId, photoId, onReported }: PhotoReportSheetProps) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await albumsApi.reportPhoto(albumId, photoId, { reason, message: message.trim() || undefined });
      toast.success('Report submitted. The album creator will review it.');
      onReported();
      handleClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error('You have already reported this photo.');
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason(null);
    setMessage('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles['backdrop']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={handleClose}
          />

          {/* Bottom sheet */}
          <motion.div
            className={styles['sheet-container']}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            <div className={styles['sheet']}>
              {/* Handle bar */}
              <div className={styles['handle-row']}>
                <div className={styles['handle']} />
              </div>

              {/* Header */}
              <div className={styles['header']}>
                <h3 className={styles['title']}>
                  Report Photo
                </h3>
                <button
                  onClick={handleClose}
                  className={styles['close-btn']}
                  aria-label="Close"
                >
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>

              {/* Reason selection */}
              <div className={styles['reasons-section']}>
                <p className={styles['reasons-prompt']}>
                  Why are you reporting this photo?
                </p>
                {REASON_OPTIONS.map((opt) => {
                  const selected = reason === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setReason(opt.value)}
                      className={[
                        styles['reason-btn'],
                        selected ? styles['reason-btn-selected'] : styles['reason-btn-default'],
                      ].join(' ')}
                    >
                      <span className={[
                        styles['reason-icon'],
                        selected ? styles['reason-icon-selected'] : styles['reason-icon-default'],
                      ].join(' ')}>
                        {opt.icon}
                      </span>
                      <div className={styles['reason-content']}>
                        <p className={[
                          styles['reason-label'],
                          selected ? styles['reason-label-selected'] : styles['reason-label-default'],
                        ].join(' ')}>
                          {opt.label}
                        </p>
                        <p className={styles['reason-desc']}>
                          {opt.description}
                        </p>
                      </div>
                      {/* Radio indicator */}
                      <span className={[
                        styles['radio-indicator'],
                        selected ? styles['radio-indicator-selected'] : styles['radio-indicator-default'],
                      ].join(' ')}>
                        {selected && (
                          <motion.span
                            className={styles['radio-dot']}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Optional message */}
              <div className={styles['message-section']}>
                <label className={styles['message-label']}>
                  Additional details (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                  placeholder="Provide more context about the issue..."
                  rows={3}
                  className={styles['message-textarea']}
                />
                <p className={styles['message-count']}>
                  {message.length}/500
                </p>
              </div>

              {/* Submit */}
              <div className={styles['submit-section']}>
                <div className={styles['submit-row']}>
                  <Button variant="ghost" size="md" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="md"
                    loading={submitting}
                    disabled={!reason}
                    onClick={handleSubmit}
                  >
                    Submit Report
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
